import { useEffect, useReducer, useRef, MutableRefObject } from 'react';
import { api } from 'services';
import type { Comment, Topic } from './types';
import { RequestArgs } from '@ff-it/api';
import { flushSync } from 'react-dom';
import useSWR from 'swr';
import type { EditorValue } from 'components/RichText/types';

const PAGE_SIZE = 15;

type ScrollAction = 'END' | 'NEW' | 'RESTORE' | 'ANCHOR' | null;

type StreamState = {
  anchor: number | null;
  items: Comment[];
  hasOlder: boolean;
  hasNewer: boolean;
  olderCursor: string | null;
  newerCursor: string | null;
  isLoading: boolean;
  error: Error | null;
};

type StreamAction =
  | { type: 'INIT'; anchor: number | null }
  | { type: 'SET_LOADING'; isLoading: boolean }
  | { type: 'SET_ERROR'; error: Error }
  | { type: 'SET_STREAM'; response: StreamResponse; append?: 'before' | 'after' }
  | { type: 'ADD_COMMENT'; comment: Comment }
  | { type: 'RESET' };

type StreamResponse = {
  items: Comment[];
  has_older: boolean;
  has_newer: boolean;
  older_cursor: string | null;
  newer_cursor: string | null;
};

const initialState: StreamState = {
  anchor: null,
  items: [],
  hasOlder: false,
  hasNewer: false,
  olderCursor: null,
  newerCursor: null,
  isLoading: true,
  error: null,
};

function streamReducer(state: StreamState, action: StreamAction): StreamState {
  switch (action.type) {
    case 'INIT':
      return { ...initialState, anchor: action.anchor };

    case 'SET_LOADING':
      return { ...state, isLoading: action.isLoading };

    case 'SET_ERROR':
      return { ...state, error: action.error, isLoading: false };

    case 'SET_STREAM': {
      const { response, append } = action;
      let items = response.items;

      if (append === 'before') {
        items = [...response.items, ...state.items];
      } else if (append === 'after') {
        items = [...state.items, ...response.items];
      }

      return {
        ...state,
        items,
        hasOlder: append === 'before' ? response.has_older : state.hasOlder,
        hasNewer: append === 'after' ? response.has_newer : state.hasNewer,
        olderCursor: append === 'before' ? response.older_cursor : state.olderCursor,
        newerCursor: append === 'after' ? response.newer_cursor : state.newerCursor,
        isLoading: false,
      };
    }

    case 'ADD_COMMENT':
      return { ...state, items: [...state.items, action.comment], isLoading: false };

    case 'RESET':
      return initialState;

    default:
      return state;
  }
}

// Helper functions for cursor encoding
function encodeAnchorCursor(anchorId: string): string {
  return btoa(JSON.stringify({ intent: 'anchor', id: anchorId }));
}

function encodeAfterCursor(comment: Comment): string {
  return btoa(JSON.stringify({ intent: 'after', ts: comment.created_at, id: comment.id }));
}

export type StreamController = {
  onCreate: (comment: EditorValue) => Promise<void>;
  items: Comment[];
  anchor: null | number;
  scrollAction: MutableRefObject<ScrollAction>;
  loadOlder?: () => void;
  loadNewer?: () => void;
  isLoading: boolean;
  error: Error | null;
};

export function useStreamController(
  topic: Topic,
  onTopicUpdate: (topic: Topic) => void,
  commentId: number | null,
): StreamController {
  const { object_key, last_created_at: topicTimestamp } = topic;
  const endpoint = `comments/${object_key}/`;
  useSWR(
    { url: `comments/${object_key}/topic/`, method: 'GET' },
    {
      // Poll for summary changes every 3 seconds.
      refreshInterval: 3000,
      // Don't re-poll if another component requests the same data within 10s.
      dedupingInterval: 3000,
      fallbackData: topic,
      onSuccess: (data) => {
        if (topic.last_activity_at !== data.last_activity_at) {
          onTopicUpdate(data);
        }
      },
    },
  );

  const scrollActionRef = useRef<ScrollAction>(null);
  const [state, dispatch] = useReducer(streamReducer, initialState);

  // Helper to make API requests
  const makeRequest = async <T>(args: RequestArgs): Promise<T> => {
    dispatch({ type: 'SET_LOADING', isLoading: true });
    try {
      const res = await api.request<T, unknown>(args);
      if (!res.ok) {
        throw new Error(`API Request Failed: ${args?.method || 'GET'} ${args.url}`);
      }
      return res.data;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', error: error as Error });
      throw error;
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: dispatch in makeRequest is stable
  useEffect(() => {
    const init = async () => {
      dispatch({ type: 'INIT', anchor: commentId });
      scrollActionRef.current = commentId ? 'ANCHOR' : 'END';

      const url = commentId
        ? `${endpoint}?cursor=${encodeURIComponent(encodeAnchorCursor(commentId.toString()))}&page_size=${PAGE_SIZE}`
        : `${endpoint}?page_size=${PAGE_SIZE}`;

      const response = await makeRequest<StreamResponse>({
        url,
        method: 'GET',
      });
      dispatch({ type: 'SET_STREAM', response });
    };

    init();
    return () => dispatch({ type: 'RESET' });
  }, [endpoint, commentId]);

  // Sync if stale
  // biome-ignore lint/correctness/useExhaustiveDependencies: dispatch in makeRequest is stable
  useEffect(() => {
    if (!topicTimestamp || state.hasNewer) return;

    const syncIfStale = async () => {
      const lastItem = state.items[state.items.length - 1];
      if (!lastItem || lastItem.created_at >= topicTimestamp) return;

      const url = `${endpoint}?cursor=${encodeURIComponent(encodeAfterCursor(lastItem))}&page_size=${PAGE_SIZE}`;

      const response = await makeRequest<StreamResponse>({
        method: 'GET',
        url,
      });
      if (response.items.length > 0) {
        dispatch({ type: 'SET_STREAM', response, append: 'after' });
        scrollActionRef.current = 'NEW';
      }
    };

    syncIfStale();
  }, [topicTimestamp, state.items, state.hasNewer, endpoint]);

  const loadOlder = state.hasOlder
    ? async () => {
        if (!state.olderCursor || state.isLoading) return;
        scrollActionRef.current = 'RESTORE';

        const url = `${endpoint}?cursor=${encodeURIComponent(state.olderCursor)}&page_size=${PAGE_SIZE}`;
        const response = await makeRequest<StreamResponse>({
          method: 'GET',
          url,
        });
        dispatch({ type: 'SET_STREAM', response, append: 'before' });
      }
    : undefined;

  const loadNewer = state.hasNewer
    ? async () => {
        if (!state.newerCursor || state.isLoading) return;
        scrollActionRef.current = 'RESTORE';

        const url = `${endpoint}?cursor=${encodeURIComponent(state.newerCursor)}&page_size=${PAGE_SIZE}`;
        const response = await makeRequest<StreamResponse>({
          method: 'GET',
          url,
        });
        dispatch({ type: 'SET_STREAM', response, append: 'after' });
      }
    : undefined;

  const onCreate = async (content: EditorValue) => {
    scrollActionRef.current = 'END';

    if (state.hasNewer) {
      // Re-fetch from top if we're not at the latest
      const url = `${endpoint}?page_size=${PAGE_SIZE}`;
      const response = await makeRequest<StreamResponse>({
        url,
        method: 'GET',
      });
      dispatch({ type: 'SET_STREAM', response });
    }

    const { comment, topic } = await makeRequest<{
      comment: Comment;
      topic: Topic;
    }>({
      url: endpoint,
      method: 'POST',
      body: { content },
    });
    flushSync(() => {
      dispatch({ type: 'ADD_COMMENT', comment });
      onTopicUpdate(topic);
    });
  };

  if (state.error) {
    throw state.error;
  }

  return {
    anchor: state.anchor,
    items: state.items,
    isLoading: state.isLoading,
    error: state.error,
    scrollAction: scrollActionRef,
    loadOlder,
    loadNewer,
    onCreate,
  };
}
